import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  TrendingUp, Users, RefreshCw, AlertTriangle,
  Wallet, UserMinus, BadgeIndianRupee, CreditCard,
  ChevronDown, ChevronRight, UserCheck
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const PLAN_COLORS = {
  starter: 'bg-sky-100 text-sky-700',
  professional: 'bg-indigo-100 text-indigo-700',
  business: 'bg-violet-100 text-violet-700',
  payg: 'bg-amber-100 text-amber-700',
  free: 'bg-zinc-100 text-zinc-500',
};

const PLAN_LABELS = {
  starter: 'Starter',
  professional: 'Professional',
  business: 'Business',
  payg: 'Pay-as-you-go',
  free: 'Free',
};

function PlanBadge({ plan }) {
  const cls = PLAN_COLORS[plan] || 'bg-zinc-100 text-zinc-500';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
      {PLAN_LABELS[plan] || plan}
    </span>
  );
}

function KpiCard({ icon: Icon, label, value, sub, iconBg, iconColor }) {
  return (
    <Card className="p-5">
      <div className="flex items-start gap-4">
        <div className={`p-2 rounded-lg shrink-0 ${iconBg}`}>
          <Icon className={`h-5 w-5 ${iconColor}`} />
        </div>
        <div className="min-w-0">
          <p className="text-sm text-zinc-500 truncate">{label}</p>
          <p className="text-2xl font-bold text-zinc-900 mt-0.5">{value}</p>
          {sub && <p className="text-xs text-zinc-400 mt-0.5">{sub}</p>}
        </div>
      </div>
    </Card>
  );
}

export default function RevenueAnalytics({ adminToken }) {
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');
  const [overview, setOverview] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [atRisk, setAtRisk] = useState([]);
  const [failures, setFailures] = useState([]);
  const [crediting, setCrediting] = useState({});
  const [showAllAtRisk, setShowAllAtRisk] = useState(false);
  const [showAllFailures, setShowAllFailures] = useState(false);

  const getAxiosConfig = useCallback(() => {
    const token = adminToken || localStorage.getItem('admin_token');
    return { headers: { Authorization: `Bearer ${token}` } };
  }, [adminToken]);

  const fetchData = useCallback(async () => {
    const axiosConfig = getAxiosConfig();
    setLoading(true);
    try {
      const [overviewRes, timelineRes, atRiskRes, failuresRes] = await Promise.all([
        axios.get(`${API}/api/admin/revenue/overview?period=${period}`, axiosConfig),
        axios.get(`${API}/api/admin/revenue/timeline?months=6`, axiosConfig),
        axios.get(`${API}/api/admin/revenue/at-risk`, axiosConfig),
        axios.get(`${API}/api/admin/revenue/payment-failures`, axiosConfig),
      ]);
      setOverview(overviewRes.data);
      setTimeline(timelineRes.data);
      setAtRisk(atRiskRes.data);
      setFailures(failuresRes.data);
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401) {
        localStorage.removeItem('admin_token');
        toast.error('Admin session expired — please log in again');
        window.location.reload();
      } else {
        toast.error(`Failed to load revenue data: ${error?.response?.data?.detail || error.message}`);
      }
    } finally {
      setLoading(false);
    }
  }, [period, getAxiosConfig]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const addRetentionCredits = async (userId, userEmail) => {
    const axiosConfig = getAxiosConfig();
    setCrediting(prev => ({ ...prev, [userId]: true }));
    try {
      await axios.post(
        `${API}/api/admin/users/${userId}/wallet`,
        { amount: 50 },
        axiosConfig
      );
      toast.success(`Added ₹50 retention credit to ${userEmail}`);
    } catch (err) {
      toast.error(`Failed to add credits: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setCrediting(prev => ({ ...prev, [userId]: false }));
    }
  };

  const fmt = (n) => `₹${(n || 0).toLocaleString('en-IN')}`;
  const fmtShort = (n) => {
    if (!n) return '₹0';
    if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
    if (n >= 1000) return `₹${(n / 1000).toFixed(1)}K`;
    return `₹${n}`;
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return iso;
    }
  };

  if (loading && !overview) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  const planBreakdown = overview?.plan_breakdown || {};
  const mrrTotal = overview?.mrr || 0;
  const planRows = ['starter', 'professional', 'business'].map(plan => ({
    plan,
    count: planBreakdown[plan]?.count || 0,
    monthly_revenue: planBreakdown[plan]?.monthly_revenue || 0,
    pct: mrrTotal > 0 ? ((planBreakdown[plan]?.monthly_revenue || 0) / mrrTotal * 100).toFixed(1) : '0.0',
  }));

  const visibleAtRisk = showAllAtRisk ? atRisk : atRisk.slice(0, 8);
  const visibleFailures = showAllFailures ? failures : failures.slice(0, 8);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-zinc-900">Revenue Analytics</h1>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="month">Last 30 days</SelectItem>
              <SelectItem value="quarter">Last 90 days</SelectItem>
              <SelectItem value="year">Last year</SelectItem>
              <SelectItem value="all">All time</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
          <KpiCard
            icon={BadgeIndianRupee}
            label="MRR"
            value={fmtShort(overview.mrr)}
            sub={`ARR ${fmtShort(overview.arr)}`}
            iconBg="bg-emerald-100"
            iconColor="text-emerald-600"
          />
          <KpiCard
            icon={TrendingUp}
            label="ARR"
            value={fmtShort(overview.arr)}
            sub="Annualised"
            iconBg="bg-indigo-100"
            iconColor="text-indigo-600"
          />
          <KpiCard
            icon={Users}
            label="Total Subscribers"
            value={overview.total_subscribers.toLocaleString()}
            sub={`+${overview.new_subscribers_this_month} this month`}
            iconBg="bg-blue-100"
            iconColor="text-blue-600"
          />
          <KpiCard
            icon={UserMinus}
            label="Churn Rate"
            value={`${overview.churn_rate}%`}
            sub={`${overview.churn_count} churned (30d)`}
            iconBg={overview.churn_rate > 5 ? "bg-red-100" : "bg-zinc-100"}
            iconColor={overview.churn_rate > 5 ? "text-red-600" : "text-zinc-500"}
          />
          <KpiCard
            icon={Wallet}
            label="Wallet Revenue Held"
            value={fmtShort(overview.wallet_revenue)}
            sub={`${overview.payg_users} PAYG users`}
            iconBg="bg-amber-100"
            iconColor="text-amber-600"
          />
        </div>
      )}

      {/* Plan Breakdown + Timeline */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Plan Breakdown Table */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-zinc-900 mb-4">Plan Breakdown</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-zinc-500 border-b border-zinc-100">
                <th className="pb-2 font-medium">Plan</th>
                <th className="pb-2 font-medium text-right">Subscribers</th>
                <th className="pb-2 font-medium text-right">Monthly Rev</th>
                <th className="pb-2 font-medium text-right">% of MRR</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-50">
              {planRows.map(row => (
                <tr key={row.plan} className="hover:bg-zinc-50 transition-colors">
                  <td className="py-3">
                    <PlanBadge plan={row.plan} />
                  </td>
                  <td className="py-3 text-right font-medium text-zinc-800">
                    {row.count.toLocaleString()}
                  </td>
                  <td className="py-3 text-right text-zinc-700">
                    {fmt(row.monthly_revenue)}
                  </td>
                  <td className="py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <div className="w-12 bg-zinc-100 rounded-full h-1.5 overflow-hidden">
                        <div
                          className="h-1.5 bg-indigo-500 rounded-full"
                          style={{ width: `${row.pct}%` }}
                        />
                      </div>
                      <span className="text-zinc-500 w-10 text-right">{row.pct}%</span>
                    </div>
                  </td>
                </tr>
              ))}
              {/* Total row */}
              <tr className="border-t border-zinc-200 font-semibold bg-zinc-50">
                <td className="py-3 text-zinc-700">Total</td>
                <td className="py-3 text-right text-zinc-900">{(overview?.total_subscribers || 0).toLocaleString()}</td>
                <td className="py-3 text-right text-zinc-900">{fmt(mrrTotal)}</td>
                <td className="py-3 text-right text-zinc-500">100%</td>
              </tr>
            </tbody>
          </table>
        </Card>

        {/* Revenue Timeline */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-zinc-900 mb-4">Revenue Timeline (last 6 months)</h3>
          {timeline.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={timeline} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v}
                />
                <Tooltip
                  formatter={(val, name) => [
                    name === 'revenue' ? `₹${val.toLocaleString('en-IN')}` : val,
                    name === 'revenue' ? 'Revenue' : name === 'new_users' ? 'New Users' : 'Churned'
                  ]}
                />
                <Legend />
                <Bar dataKey="revenue" fill="#6366f1" name="Revenue (₹)" radius={[3, 3, 0, 0]} />
                <Bar dataKey="new_users" fill="#22c55e" name="New Users" radius={[3, 3, 0, 0]} />
                <Bar dataKey="churned" fill="#f87171" name="Churned" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-48 text-zinc-400 text-sm">
              No timeline data available
            </div>
          )}
        </Card>
      </div>

      {/* At-Risk Users */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            <h3 className="text-lg font-semibold text-zinc-900">At-Risk Subscribers</h3>
            {atRisk.length > 0 && (
              <span className="bg-amber-100 text-amber-700 text-xs font-medium px-2 py-0.5 rounded-full">
                {atRisk.length} users
              </span>
            )}
          </div>
          <p className="text-xs text-zinc-400">Paid plan users inactive for 14+ days</p>
        </div>

        {atRisk.length === 0 ? (
          <div className="flex items-center gap-2 text-emerald-600 text-sm py-4">
            <UserCheck className="h-4 w-4" />
            All paid subscribers are active. No at-risk users.
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-zinc-500 border-b border-zinc-100">
                    <th className="pb-2 font-medium">User</th>
                    <th className="pb-2 font-medium">Plan</th>
                    <th className="pb-2 font-medium text-right">Days Inactive</th>
                    <th className="pb-2 font-medium">Last Login</th>
                    <th className="pb-2 font-medium text-right">Wallet</th>
                    <th className="pb-2 font-medium text-right">Usage</th>
                    <th className="pb-2 font-medium text-center">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-50">
                  {visibleAtRisk.map((user) => (
                    <tr key={user.id} className="hover:bg-zinc-50 transition-colors">
                      <td className="py-3">
                        <div className="font-medium text-zinc-800 truncate max-w-[160px]">{user.name || '—'}</div>
                        <div className="text-zinc-400 text-xs truncate max-w-[160px]">{user.email}</div>
                      </td>
                      <td className="py-3">
                        <PlanBadge plan={user.plan} />
                      </td>
                      <td className="py-3 text-right">
                        <span className={`font-semibold ${user.days_inactive > 30 ? 'text-red-600' : 'text-amber-600'}`}>
                          {user.days_inactive != null ? `${user.days_inactive}d` : '—'}
                        </span>
                      </td>
                      <td className="py-3 text-zinc-500 text-xs">{formatDate(user.last_login)}</td>
                      <td className="py-3 text-right text-zinc-700">{fmt(user.wallet_balance)}</td>
                      <td className="py-3 text-right text-zinc-500">
                        {user.monthly_quota > 0
                          ? `${((user.monthly_usage / user.monthly_quota) * 100).toFixed(0)}%`
                          : '—'}
                      </td>
                      <td className="py-3 text-center">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                          disabled={crediting[user.id]}
                          onClick={() => addRetentionCredits(user.id, user.email)}
                        >
                          {crediting[user.id] ? (
                            <RefreshCw className="h-3 w-3 animate-spin" />
                          ) : (
                            <>
                              <CreditCard className="h-3 w-3 mr-1" />
                              +₹50 Credits
                            </>
                          )}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {atRisk.length > 8 && (
              <button
                className="mt-3 flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                onClick={() => setShowAllAtRisk(v => !v)}
              >
                {showAllAtRisk ? (
                  <><ChevronDown className="h-4 w-4" /> Show less</>
                ) : (
                  <><ChevronRight className="h-4 w-4" /> Show all {atRisk.length} users</>
                )}
              </button>
            )}
          </>
        )}
      </Card>

      {/* Payment Failures */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-red-500" />
            <h3 className="text-lg font-semibold text-zinc-900">Payment Failures</h3>
            {failures.length > 0 && (
              <span className="bg-red-100 text-red-700 text-xs font-medium px-2 py-0.5 rounded-full">
                {failures.length}
              </span>
            )}
          </div>
          <p className="text-xs text-zinc-400">Failed or expired subscription orders</p>
        </div>

        {failures.length === 0 ? (
          <div className="text-zinc-400 text-sm py-4">No payment failures found.</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-zinc-500 border-b border-zinc-100">
                    <th className="pb-2 font-medium">User</th>
                    <th className="pb-2 font-medium">Plan</th>
                    <th className="pb-2 font-medium text-right">Amount</th>
                    <th className="pb-2 font-medium">Date</th>
                    <th className="pb-2 font-medium">Status</th>
                    <th className="pb-2 font-medium">Order ID</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-50">
                  {visibleFailures.map((f, idx) => (
                    <tr key={f.order_id || idx} className="hover:bg-zinc-50 transition-colors">
                      <td className="py-3 text-zinc-700 truncate max-w-[160px]">
                        {f.user_email || '—'}
                      </td>
                      <td className="py-3">
                        {f.plan ? <PlanBadge plan={f.plan} /> : <span className="text-zinc-400">—</span>}
                      </td>
                      <td className="py-3 text-right font-medium text-zinc-800">
                        {f.amount ? fmt(f.amount) : '—'}
                      </td>
                      <td className="py-3 text-zinc-500 text-xs">{formatDate(f.created_at)}</td>
                      <td className="py-3">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                          {f.status}
                        </span>
                      </td>
                      <td className="py-3 text-zinc-400 text-xs font-mono truncate max-w-[120px]">
                        {f.order_id || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {failures.length > 8 && (
              <button
                className="mt-3 flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                onClick={() => setShowAllFailures(v => !v)}
              >
                {showAllFailures ? (
                  <><ChevronDown className="h-4 w-4" /> Show less</>
                ) : (
                  <><ChevronRight className="h-4 w-4" /> Show all {failures.length} failures</>
                )}
              </button>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
